/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useEffect, useState } from 'react';
import {
  InputAdornment, InputLabel, MenuItem, Select, TextField,
} from '@mui/material';
import { ExpandUpIcon } from 'src/icons';
import { CommonElementProps } from '../types';
import ElementUtils from '../utils';

export interface InputProps<T, V> extends CommonElementProps<V> {
  label?: string;
  iconStart?: T;
  helperText?: string;
  type?: string;
  value?: V;
  disabled?: boolean;
  onChange?: (value: V) => void;
  onCurrencyChange?: (currency: Option) => void;
  placeholder?: string;
}

export interface Option {
  currencyName: string;
  currencyIcon: string;
}
export const options: Option[] = [
  {
    currencyName: 'USD',
    currencyIcon: '$',
  },
  {
    currencyName: 'INR',
    currencyIcon: '₹',
  },
  {
    currencyName: 'EUR',
    currencyIcon: '€',
  },
  {
    currencyName: 'YEN',
    currencyIcon: '¥',
  },
];
function Input<T, V>(props: InputProps<T, V>) {
  const {
    iconStart,
    label,
    type = 'string',
    helperText,
    value,
    disabled,
    onChange,
    error,
    onCurrencyChange,
    placeholder,
  } = props;

  const handleValueChange = (event: any) => {
    if (!disabled && onChange) {
      onChange(event.target.value as V);
    }
  };

  const iconStartCurrency = options.find(
    (option) => option.currencyIcon === iconStart,
  ) || options[0];

  const [currency, setCurrency] = useState(iconStartCurrency || options[0]);

  const handleCurrencyChange = (event: any) => {
    setCurrency(event.target.value as typeof options[0]);
    if (onCurrencyChange) {
      onCurrencyChange(event.target.value as typeof options[0]);
    }
  };

  useEffect(() => {
    setCurrency(iconStartCurrency || options[0]);
  }, [iconStart]);

  return (
    <div className="flex flex-col justify-center items-start gap-1">
      {label && (
        <InputLabel className="font-bold text-[#28364C] fontSize-[1rem]" error={!!error}>
          {label}
        </InputLabel>
      )}

      <TextField
        placeholder={placeholder || label}
        id="outlined-basic"
        color="primary"
        defaultValue={value}
        value={value}
        type={type}
        variant="outlined"
        fullWidth
        helperText={helperText}
        disabled={disabled}
        onChange={handleValueChange}
        sx={{
          '& fieldset': { border: 'none' },
        }}
        error={!!error}
        InputProps={{
          startAdornment: iconStart ? (
            <div className="h-[2.5rem] overflow-clip min-w-[6.25rem] flex justify-center items-center rounded-[0.625rem]">
              <InputAdornment
                position="start"
              >
                <Select
                  value={currency}
                  onChange={handleCurrencyChange}
                  disabled={disabled}
                  IconComponent={ExpandUpIcon}
                  style={{
                    backgroundColor: '#F6F6F6',
                    borderTopLeftRadius: '4px',
                    borderBottomLeftRadius: '4px',
                    fontSize: '12px',
                  }}
                  inputProps={{ 'aria-label': 'Without label' }}
                  classes={{

                    icon: 'mr-0 mb-2 w-3 h-3',
                  }}
                >
                  {options.map((option) => (
                    <MenuItem key={option.currencyName} className="text-[#28364C]" value={option as any}>
                      {option.currencyName}
                    </MenuItem>
                  ))}
                </Select>
                <div
                  style={{
                    borderTopLeftRadius: '4px',
                    borderBottomLeftRadius: '4px',
                    padding: '10px',
                    fontSize: '20px',
                  }}
                  data-testid="currency-icon"
                >
                  {currency.currencyIcon}
                </div>
              </InputAdornment>
            </div>
          ) : null,
          classes: {
            root: disabled ? 'pl-0 text-[#28364C] rounded-md' : 'bg-white custom-shadow pl-0 rounded-md',
            error: 'outline outline-[1px] outline-error',
          },
        }}
      />
      {error && (
        <div className="text-error text-xs">
          {ElementUtils.renderError(error)}
        </div>
      )}
    </div>
  );
}

Input.defaultProps = {
  label: '',
  iconStart: '',
  placeholder: '',
  helperText: '',
  value: '',
  type: 'string',
  disabled: false,
  onChange: () => { },
  onCurrencyChange: () => { },
};

export default Input;
